import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';
import { useSelector } from 'react-redux';
import SimpleBar from '../../../../../components/third-party/SimpleBar';
import MiniDrawerStyled from '../../../../../layout/MainLayout/NewDrawer/MiniDrawerStyled';
import { drawerWidth, miniDrawerWidth } from '../../../../../config';
import menuConfig from '../../../../config/menu.config';
import SidebarHeader from './SidebarHeader';
import Navigation from './Navigation';

const Sidebar = ({ open, handleDrawerToggle, window }) => {
  const theme = useTheme();
  const matchDownMD = useMediaQuery(theme.breakpoints.down('md'));

  const { activeSmallScreenMenu } = useSelector(state => state.menu);

  // Memoize container calculation
  const container = useMemo(
    () => (window !== undefined ? () => window().document.body : undefined),
    [window]
  );

  // Memoize subChildren check
  const hasSubChildren = useMemo(() => {
    if (!activeSmallScreenMenu?.length) return false;
    const childItem = menuConfig.children.find(
      op => op.id === activeSmallScreenMenu[0]
    );
    return Boolean(childItem?.subChildren);
  }, [activeSmallScreenMenu]);


  // Memoize drawer content
  const drawerContent = useMemo(() => (
    <SimpleBar
      sx={{
        '& .simplebar-content': {
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        },
        height: 'calc(100vh - 68px)'
      }}
    >
      <Navigation />
    </SimpleBar>
  ), []);

  // Memoize drawer header
  const drawerHeader = useMemo(() => (
    <SidebarHeader
      open={open}
      handleDrawerToggle={handleDrawerToggle}
      matchDownMD={matchDownMD}
    />
  ), [open, handleDrawerToggle, matchDownMD]);

  // Memoize drawer styles
  const mobileDrawerStyles = useMemo(() => ({
    display: { xs: 'block', lg: 'none' },
    '& .MuiDrawer-paper': {
      boxSizing: 'border-box',
      width: hasSubChildren ? drawerWidth : miniDrawerWidth,
      borderRight: `1px solid ${theme?.palette?.divider}`,
      backgroundImage: 'none',
      backgroundColor: theme?.palette?.primary?.main,
      boxShadow: 'inherit'
    }
  }), [theme, hasSubChildren]);

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 }, zIndex: 1300 }}
      aria-label="mailbox folders"
    >
      {!matchDownMD ? (
        <MiniDrawerStyled variant="permanent" open={open}>
          {drawerHeader}
          {drawerContent}
        </MiniDrawerStyled>
      ) : (
        <Drawer
          container={container}
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
            BackdropProps: {
              sx: {
                backgroundColor: 'transparent'
              }
            }
          }}
          sx={mobileDrawerStyles}
        >
          {open && (
            <>
              {drawerHeader}
              {drawerContent}
            </>
          )}
        </Drawer>
      )}
    </Box>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func.isRequired,
  window: PropTypes.func
};

export default memo(Sidebar);

